import { create } from 'zustand';
import { requestCheckout, type CheckoutLink } from '../lib/checkoutClient';
import { supabase } from '../lib/supabase';
import { getAnalyticsAcquisitionCampaign } from '../lib/acquisitionCampaign';
import type { Database } from '../types/supabase';

type Subscription = Database['public']['Tables']['subscriptions']['Row'];

export type Plan = 'free' | 'pro';

export const PLAN_CONFIG = {
  free: {
    ads: false,
    maxExportSampleRate: 44100,
    maxExportBitDepth: 24,
    cloudProjectSave: true,
    maxCloudProjects: 5,
    // Total cloud audio storage cap per user. Sum of audio_files.file_size
    // is checked before each upload — when adding the new file would push
    // the total over this number, the upload is rejected and PricingModal
    // is shown instead.
    maxStorageBytes: 300 * 1024 * 1024, // 300 MB
    sampleStoreDownload: false,
    batchProcessing: false,
  },
  pro: {
    ads: false,
    maxExportSampleRate: 96000,
    maxExportBitDepth: 32,
    cloudProjectSave: true,
    maxCloudProjects: Infinity,
    maxStorageBytes: Infinity,
    sampleStoreDownload: true,
    batchProcessing: true,
  },
} as const;

interface SubscriptionState {
  subscription: Subscription | null;
  plan: Plan;
  isLoading: boolean;

  // Actions
  fetchSubscription: (userId: string) => Promise<void>;
  createCheckoutSession: (currency: 'jpy' | 'usd', source?: string, interval?: 'month' | 'year', promo?: string, email?: string) => Promise<CheckoutLink | null>;
  createPassCheckout: (pass?: 'day' | 'month', email?: string, source?: string) => Promise<CheckoutLink | null>;
  createPortalSession: () => Promise<string | null>;
  reset: () => void;

  // Helpers
  shouldShowAd: () => boolean;
}

// QA view override — `?qa_plan=free` (sticky via localStorage, cleared with
// `?qa_plan=off`). Paywall, pricing modal and every Pro gate are invisible to a
// signed-in Pro account, so money-path UI could not be exercised in a browser
// at all without signing out of an account we cannot sign back into.
//
// DOWNGRADE ONLY, deliberately: 'pro' is not an accepted value. Forcing the
// view to 'free' can only ever ADD gates, never unlock anything, so this cannot
// be used to take Pro features without paying. Entitlements that matter
// (Stripe, the subscriptions table, server routes) never consult this.
function qaPlanOverride(): Plan | null {
  if (typeof window === 'undefined') return null;
  try {
    const requested = new URLSearchParams(window.location.search).get('qa_plan');
    if (requested === 'off') {
      localStorage.removeItem('la_qa_plan');
      return null;
    }
    if (requested === 'free') localStorage.setItem('la_qa_plan', 'free');
    return localStorage.getItem('la_qa_plan') === 'free' ? 'free' : null;
  } catch {
    return null;
  }
}

function derivePlan(subscription: Subscription | null): Plan {
  const override = qaPlanOverride();
  if (override) return override;
  if (!subscription) return 'free';
  const activeStatuses = ['active', 'trialing'];
  return activeStatuses.includes(subscription.status) ? 'pro' : 'free';
}

export const useSubscriptionStore = create<SubscriptionState>((set, get) => ({
  subscription: null,
  plan: 'free',
  isLoading: false,

  fetchSubscription: async (userId: string) => {
    set({ isLoading: true });
    try {
      const { data: sub, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        throw error;
      }

      set({
        subscription: sub,
        plan: derivePlan(sub),
        isLoading: false,
      });
    } catch {
      set({ isLoading: false });
    }
  },

  createCheckoutSession: async (currency, source, interval, promo, email) => {
    const acquisitionCampaign = getAnalyticsAcquisitionCampaign();
    return requestCheckout('/api/stripe/create-checkout-session', {
      currency, source, ...(interval ? { interval } : {}), ...(promo ? { promo } : {}),
      ...(email ? { email } : {}), ...(acquisitionCampaign ? { acquisitionCampaign } : {}),
    });
  },

  createPassCheckout: async (pass = 'month', email, source = 'unknown') =>
    requestCheckout('/api/stripe/create-pass-checkout', { pass, source, ...(email ? { email } : {}) }),

  createPortalSession: async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) return null;

      const res = await fetch('/api/stripe/create-portal-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (!res.ok) return null;
      const data = await res.json() as { url: string };
      return data.url;
    } catch {
      return null;
    }
  },

  reset: () => {
    set({ subscription: null, plan: 'free', isLoading: false });
  },

  shouldShowAd: () => {
    // AD GATE PAUSED (2026-08-05): HilltopAds video gate is disabled for the
    // duration of the AdSense re-review — reviewers hitting a video-ad gate
    // on tool pages risks another "low value content" verdict. Everyone gets
    // the (well-tested) Pro no-ads path. Revert this line once the review
    // decision lands.
    return false;
    // return PLAN_CONFIG[get().plan].ads;
  },
}));

// Dev-only: expose the store for browser-automation E2E (plan overrides).
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  (window as unknown as { __SUB_STORE__?: typeof useSubscriptionStore }).__SUB_STORE__ =
    useSubscriptionStore;
}
